import { useAssetCondition, useConditionByType } from "@features/ax/hooks";
import {
  useToggleConditionParameter,
  useUpdateCondition,
} from "@features/ax/settings/assetClassification/hooks";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Slider } from "@shared/ui/slider";
import { Switch } from "@shared/ui/switch";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface ApiConditionParameter {
  name: string;
  score: number;
  isActive: boolean;
  createdBy: string;
  description: string;
  createdDate: string;
  code: string;
  weightage: number;
}

interface ConditionParameter extends ApiConditionParameter {
  sliderValue: number;
  weightValue: number;
}

interface ConditionAssessmentModalProps {
  open: boolean;
  onClose: () => void;
  existingParameters?: ConditionParameter[];
  existingScore?: number;
  title?: string;
  assetName?: string;
  assetClassCode?: string;
  assetCode?: string;
  assetParameters?: any[];
}

const ConditionAssessmentModal: React.FC<ConditionAssessmentModalProps> = ({
  open,
  onClose,
  existingParameters,
  existingScore,
  title = "Condition Assessment",
  assetName,
  assetClassCode: assetClassCode,
  assetCode,
  assetParameters,
}) => {
  const { toast } = useToast();
  const initializationRef = useRef(false);

  const remoteUtilityId = useRemoteUtilityId();

  const { data: conditionData, isLoading: isConditionLoading } =
    useAssetCondition();

  const { mutateAsync: updateCondition, isPending: isUpdating } =
    useUpdateCondition();

  const { mutateAsync: toggleConditionParameter, isPending: isToggling } =
    useToggleConditionParameter();

  const {
    data: conditionByType,
    isLoading: isConditionByTypeLoading,
    refetch,
  } = useConditionByType(assetClassCode, assetCode);

  const [conditionParameters, setConditionParameters] = useState<
    ConditionParameter[]
  >([]);
  const [overallConditionScore, setOverallConditionScore] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  const assessmentScores = [
    { score: 5, text: "Excellent condition, minimal maintenance needed" },
    { score: 4, text: "Good condition, routine maintenance recommended" },
    { score: 3, text: "Fair condition, preventive maintenance required" },
    { score: 2, text: "Poor condition, corrective maintenance needed" },
    { score: 1, text: "Critical condition, immediate attention required" },
  ];

  // Initialize parameters when modal opens or when condition data is loaded
  useEffect(() => {
    if (initializationRef.current || !conditionData || isConditionLoading) {
      return;
    }

    let mergedParameters: ConditionParameter[] = [];

    if (Array.isArray(conditionData)) {
      if (Array.isArray(conditionByType?.[0]?.assetParameters)) {
        console.log("Merging condition data with asset details");
        // Create map for faster lookup
        const assetParamMap = conditionByType[0].assetParameters.reduce(
          (map: any, param: any) => {
            map[param.parameter] = param;
            return map;
          },
          {}
        );

        // Merge condition data with asset detail values
        mergedParameters = conditionData.map(
          (conditionParam: ApiConditionParameter) => {
            const assetParam = assetParamMap[conditionParam.name];
            return {
              ...conditionParam,
              sliderValue: assetParam?.score ?? 3,
              weightValue: assetParam?.weightValue ?? conditionParam.score,
              weightage: conditionParam.weightage,
              isActive: conditionParam.isActive,
            };
          }
        );
      } else {
        // Use condition data with defaults
        mergedParameters = conditionData.map(
          (param: ApiConditionParameter) => ({
            ...param,
            sliderValue: 3,
            weightValue: param.score,
          })
        );
      }

      setConditionParameters(mergedParameters);
      initializationRef.current = true;

      // Try to restore saved data after initial setup
    }
  }, [conditionData, conditionByType, isConditionLoading]);

  // Reset initialization when modal closes
  useEffect(() => {
    if (!open) {
      initializationRef.current = false;
      setIsInitialized(false);
    }
  }, [open]);

  // Memoize calculations to prevent excessive recalculation
  const calculationResults = useMemo(() => {
    const activeParams = conditionParameters.filter((p) => p.isActive);

    if (activeParams.length === 0) {
      return { overallScore: 0, enabledCount: 0 };
    }

    const totalWeight = activeParams.reduce((sum, p) => sum + p.weightValue, 0);
    const weightedScore = activeParams.reduce(
      (sum, p) => sum + (p.sliderValue || 3) * p.weightValue,
      0
    );

    const averageScore = totalWeight > 0 ? weightedScore / totalWeight : 0;
    const conditionScore = Math.round(averageScore * 20);

    return { overallScore: conditionScore, enabledCount: activeParams.length };
  }, [conditionParameters]);

  // Update overall score when calculations change
  useEffect(() => {
    const { overallScore } = calculationResults;
    setOverallConditionScore(overallScore);
  }, [calculationResults]);

  // Optimized SliderComponent with local state management
  const SliderComponent = React.memo(
    ({
      param,
      onScoreChange,
    }: {
      param: ConditionParameter;
      onScoreChange: (code: string, score: number) => void;
    }) => {
      const [localValue, setLocalValue] = useState(param.sliderValue || 3);

      // Update local state when param changes
      useEffect(() => {
        setLocalValue(param.sliderValue || 3);
      }, [param.sliderValue]);

      const handleChange = useCallback(
        (newValue: number[]) => {
          const score = newValue[0];
          setLocalValue(score);
          onScoreChange(param.code, score);
        },
        [param.code, onScoreChange]
      );

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Value: {localValue}
            </span>
            <span className="text-sm text-gray-500">Max: 5</span>
          </div>
          <Slider
            min={1}
            max={5}
            step={1}
            value={[localValue]}
            onValueChange={handleChange}
            disabled={!param.isActive}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Critical</span>
            <span>Poor</span>
            <span>Fair</span>
            <span>Good</span>
            <span>Excellent</span>
          </div>
        </div>
      );
    }
  );

  // Optimized update functions
  const updateParameterScore = useCallback(
    (parameterCode: string, sliderValue: number) => {
      setConditionParameters((prev) =>
        prev.map((p) => (p.code === parameterCode ? { ...p, sliderValue } : p))
      );
    },
    []
  );

  const updateParameterWeight = useCallback(
    (parameterCode: string, weightValue: number) => {
      setConditionParameters((prev) =>
        prev.map((p) => (p.code === parameterCode ? { ...p, weightValue } : p))
      );
    },
    []
  );

  const toggleParameter = useCallback(
    (parameterCode: string, value: boolean) => {
      toggleConditionParameter({
        code: parameterCode,
        is_active: value,
        remoteUtilityId: remoteUtilityId,
      });
      setConditionParameters((prev) =>
        prev.map((p) =>
          p.code === parameterCode ? { ...p, isActive: !p.isActive } : p
        )
      );
    },
    []
  );

  // Memoized weightage calculation
  const calculateWeightagePercentage = useCallback(
    (paramWeight: number): string => {
      const enabledParams = conditionParameters.filter((p) => p.isActive);
      const totalWeight = enabledParams.reduce(
        (sum, p) => sum + p.weightValue,
        0
      );
      return totalWeight > 0
        ? ((paramWeight / totalWeight) * 100).toFixed(1)
        : "0";
    },
    [conditionParameters]
  );

  const getConditionGrade = useCallback((score: number) => {
    if (score >= 80)
      return {
        grade: "Excellent",
        color: "text-green-700",
        bgColor: "bg-green-100",
      };
    if (score >= 60)
      return {
        grade: "Good",
        color: "text-lime-700",
        bgColor: "bg-lime-100",
      };
    if (score >= 40)
      return {
        grade: "Fair",
        color: "text-yellow-700",
        bgColor: "bg-yellow-100",
      };
    if (score >= 20)
      return {
        grade: "Poor",
        color: "text-orange-700",
        bgColor: "bg-orange-100",
      };
    return {
      grade: "Critical",
      color: "text-red-700",
      bgColor: "bg-red-100",
    };
  }, []);

  const handleSave = async () => {
    const assetParameters = conditionParameters.map((item) => ({
      parameter: item.name,
      score: item.sliderValue ?? 3,
      weightage: item.weightage.toString(),
      weight_value: item.weightValue,
      is_active: item.isActive,
    }));

    const payload = {
      code: assetCode,
      name: assetName,
      remote_utility_id: remoteUtilityId,
      asset_type_characteristics: "asset_type_condition",
      condition_scores: overallConditionScore,
      asset_parameters: assetParameters,
    };

    await updateCondition(payload);
    refetch();
    onClose();
  };

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Memoize expensive calculations
  const conditionGrade = useMemo(
    () => getConditionGrade(overallConditionScore),
    [overallConditionScore, getConditionGrade]
  );

  const enabledParameters = useMemo(
    () => conditionParameters.filter((p) => p.isActive),
    [conditionParameters]
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {title}
            {assetName && (
              <span className="text-gray-600 font-normal ml-2">
                - {assetName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Assessment Parameters */}
          <div>
            <h3 className="text-lg font-medium mb-4">Assessment Parameters</h3>
            <p className="text-sm text-gray-600 mb-6">
              Review each parameter. Use the toggle to enable/disable
              parameters, and adjust scores (1-5) and weights as needed. Higher
              scores indicate better condition. Inactive parameters are shown
              but do not affect the overall score.
            </p>

            {conditionParameters.length > 0 ? (
              <div className="space-y-4">
                {conditionParameters.map((param) => (
                  <div
                    key={param.code}
                    className={`p-4 border rounded-lg ${
                      param.isActive ? "bg-white" : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Switch
                            checked={param.isActive}
                            onCheckedChange={(value) =>
                              toggleParameter(param.code, value)
                            }
                          />
                          <h4
                            className={`font-medium ${
                              param.isActive ? "text-gray-900" : "text-gray-500"
                            }`}
                          >
                            {param.name}
                          </h4>
                          {param.isActive && (
                            <Badge variant="outline" className="ml-auto">
                              {calculateWeightagePercentage(param.weightValue)}%
                            </Badge>
                          )}
                          {!param.isActive && (
                            <Badge variant="secondary" className="ml-auto">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p
                          className={`text-sm ${
                            param.isActive ? "text-gray-600" : "text-gray-400"
                          }`}
                        >
                          {param.description}
                        </p>
                      </div>
                    </div>

                    {param.isActive && (
                      <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                          {/* Score Section with Slider */}
                          <div className="lg:col-span-2 space-y-3">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              Condition Score (1-5)
                              <span className="text-lg font-semibold text-blue-600">
                                {param.sliderValue || 3}
                              </span>
                            </Label>
                            <SliderComponent
                              param={param}
                              onScoreChange={updateParameterScore}
                            />
                          </div>

                          {/* Weight Section */}
                          <div className="space-y-3">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              Weight (%)
                              <span className="text-sm text-gray-500">
                                (
                                {calculateWeightagePercentage(
                                  param.weightValue
                                )}
                                %)
                              </span>
                            </Label>
                            <Select
                              value={param.weightValue.toString()}
                              onValueChange={(value) =>
                                updateParameterWeight(
                                  param.code,
                                  parseInt(value)
                                )
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">
                                  1 - Low Importance
                                </SelectItem>
                                <SelectItem value="2">
                                  2 - Below Average
                                </SelectItem>
                                <SelectItem value="3">3 - Average</SelectItem>
                                <SelectItem value="4">
                                  4 - Above Average
                                </SelectItem>
                                <SelectItem value="5">5 - Critical</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">
                              Higher weight = more influence on overall score
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No condition parameters available.
              </div>
            )}

            {/* Overall Condition Score Display */}
            <div
              className={`p-6 rounded-lg border-2 ${conditionGrade.bgColor} mt-6`}
            >
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2">
                  Overall Condition Score
                </h3>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <span className="text-4xl font-bold">
                    {overallConditionScore}
                  </span>
                  <span className="text-lg text-gray-500">/ 100</span>
                  <Badge
                    className={`text-lg px-4 py-2 ${conditionGrade.color}`}
                    variant="outline"
                  >
                    {conditionGrade.grade}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Based on {enabledParameters.length} active assessment
                  parameters out of {conditionParameters.length} total. Adjust
                  individual scores and weights below to refine the assessment.
                </p>
              </div>
            </div>

            {/* Assessment Guidelines */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">
                Assessment Guidelines
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {assessmentScores.map(({ score, text }) => (
                  <li key={score}>
                    • <strong>Score {score}:</strong> {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <DialogFooter className="sticky bottom-0 bg-white border-t flex justify-end">
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} isLoading={isUpdating}>
                Save Assessment
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConditionAssessmentModal;
